import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  fetchCasaMapaPartnerMarketAreas,
  fetchCasaMapaPartnerPulseDigest,
  fetchCasaMapaPartnerPulseDigests,
  fetchCasaMapaPartnerPulsePrefs,
  generateCasaMapaPartnerPulseDigest,
  updateCasaMapaPartnerPulsePrefs,
} from "../../services/casamapaPartnerAgencyApi";
import { useLocale } from "../../intl";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerCasaMapaMarketTools.css";

const COPY = "realEstate.casaMapa.admin.partnerPulse";

export default function PartnerCasaMapaPulsePage() {
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const [prefs, setPrefs] = useState(null);
  const [areas, setAreas] = useState(null);
  const [digests, setDigests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [prefsResult, digestsResult, areasResult] = await Promise.all([
        fetchCasaMapaPartnerPulsePrefs(),
        fetchCasaMapaPartnerPulseDigests(),
        fetchCasaMapaPartnerMarketAreas().catch(() => null),
      ]);
      setPrefs(prefsResult.prefs || null);
      setDigests(digestsResult.digests || []);
      setAreas(areasResult);
    } catch (err) {
      setError(err?.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSavePrefs(event) {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      const result = await updateCasaMapaPartnerPulsePrefs(prefs);
      setPrefs(result.prefs);
    } catch (err) {
      setError(err?.message || t(`${COPY}.saveError`));
    } finally {
      setSaving(false);
    }
  }

  async function handleGenerate() {
    try {
      setSaving(true);
      setError("");
      const result = await generateCasaMapaPartnerPulseDigest({ sendEmail: false });
      setSelected(result.digest || null);
      await load();
    } catch (err) {
      setError(err?.message || t(`${COPY}.generateError`));
    } finally {
      setSaving(false);
    }
  }

  async function openDigest(digestId) {
    try {
      setError("");
      const result = await fetchCasaMapaPartnerPulseDigest(digestId);
      setSelected(result.digest || null);
    } catch (err) {
      setError(err?.message || t(`${COPY}.loadError`));
    }
  }

  const municipalities = areas?.municipalities || [];

  return (
    <div className="cm-partner-market-tool">
      <header className="partner-dashboard-hero">
        <div>
          <p className="cm-partner-market-tool__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="partner-dashboard-hero__intro">{t(`${COPY}.subtitle`)}</p>
        </div>
        <div className="partner-dashboard-hero__actions">
          <Link className="partner-dashboard-btn" to={path("market")}>
            {t(`${COPY}.back`)}
          </Link>
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={handleGenerate}
            disabled={saving}
          >
            {t(`${COPY}.generateNow`)}
          </button>
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {prefs && !loading ? (
        <form className="partner-panel cm-partner-pulse__prefs" onSubmit={handleSavePrefs}>
          <h2>{t(`${COPY}.prefsTitle`)}</h2>
          <p className="muted">{t(`${COPY}.prefsIntro`)}</p>
          <label className="cm-partner-pulse__check">
            <input
              type="checkbox"
              checked={prefs.emailEnabled !== false}
              onChange={(e) => setPrefs((prev) => ({ ...prev, emailEnabled: e.target.checked }))}
            />
            <span>{t(`${COPY}.emailEnabled`)}</span>
          </label>
          <fieldset>
            <legend>{t(`${COPY}.municipalities`)}</legend>
            <div className="cm-partner-pulse__chips">
              {municipalities.map((row) => {
                const checked = (prefs.municipalities || []).includes(row.name);
                return (
                  <label key={row.name}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setPrefs((prev) => {
                          const current = new Set(prev.municipalities || []);
                          if (e.target.checked) current.add(row.name);
                          else current.delete(row.name);
                          return { ...prev, municipalities: [...current] };
                        });
                      }}
                    />
                    {row.name}
                  </label>
                );
              })}
            </div>
          </fieldset>
          <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary" disabled={saving}>
            {t(`${COPY}.savePrefs`)}
          </button>
        </form>
      ) : null}

      <section className="partner-panel">
        <h2>{t(`${COPY}.archiveTitle`)}</h2>
        {digests.length ? (
          <ul className="cm-partner-pulse__archive">
            {digests.map((digest) => (
              <li key={digest.digestId}>
                <button type="button" onClick={() => openDigest(digest.digestId)}>
                  {digest.periodStart} → {digest.periodEnd}
                </button>
                <span className="muted">{digest.emailStatus}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t(`${COPY}.archiveEmpty`)}</p>
        )}
      </section>

      {selected ? (
        <article className="partner-panel">
          <h2>
            {selected.periodStart} → {selected.periodEnd}
          </h2>
          <ol className="cm-partner-heatmap__list">
            {(selected.payload?.topBarrios || []).map((row) => (
              <li key={row.boundaryId || `${row.municipality}:${row.neighborhood}`}>
                <div className="cm-partner-heatmap__row">
                  <div>
                    <strong>{row.neighborhood}</strong>
                    <span className="muted"> · {row.municipality}</span>
                  </div>
                  <div className="cm-partner-heatmap__stats">
                    <span>{row.listingViews} views</span>
                    <span>{row.inquiries} inquiries</span>
                  </div>
                </div>
                <Link
                  className="cm-partner-heatmap__brief-link"
                  to={`${path("market/brief")}?municipality=${encodeURIComponent(row.municipality || "")}&neighborhood=${encodeURIComponent(row.neighborhood || "")}${row.boundaryId ? `&boundaryId=${encodeURIComponent(row.boundaryId)}` : ""}`}
                >
                  {t("realEstate.casaMapa.admin.partnerHeatmap.briefLink")}
                </Link>
              </li>
            ))}
          </ol>
        </article>
      ) : null}
    </div>
  );
}
